/**
 * InboxPage.tsx — 1804va rev2
 * Recipient portal: pushed content, preview, license request, download on approval
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Button, Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'
import { useAuthStore } from '@/stores/authStore'
import { Download } from 'lucide-react'

type PushItem = {
  id: string
  match_score: number
  match_reason: string
  status: string
  queued_at: string
  media_items: {
    id: string
    cloudinary_url: string
    media_type: string
    file_name: string
    ai_tags: string[]
    ai_description: string
    ai_mood: string
    ai_route_score: number
    participants: { name: string; handle: string } | null
    rights_profiles: Array<{
      usage_type: string
      credit_required: boolean
      embargo_until: string | null
    }>
  }
}

type License = {
  id: string
  media_item_id: string
  download_url: string | null
}

function WatermarkedPreview({ url, mediaType }: { url: string; mediaType: string }) {
  if (mediaType === 'photo') {
    return (
      <div className="relative aspect-video bg-neutral-900 rounded-t-xl overflow-hidden">
        <img
          src={url.replace('/upload/', '/upload/e_blur:400,o_50/')}
          alt="preview"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white/30 text-3xl font-black tracking-widest rotate-[-20deg] select-none pointer-events-none">
            THE ROOM
          </span>
        </div>
        <div className="absolute bottom-2 right-2">
          <span className="text-xs bg-black/60 text-neutral-400 px-2 py-0.5 rounded-full">Preview</span>
        </div>
      </div>
    )
  }
  return (
    <div className="aspect-video bg-neutral-900 rounded-t-xl flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl mb-2">{mediaType === 'video' ? '🎬' : mediaType === 'audio' ? '🎵' : '📄'}</div>
        <p className="text-neutral-500 text-sm">Request license to access</p>
      </div>
    </div>
  )
}

function PushCard({ item, participantId, approvedLicenses }: {
  item: PushItem
  participantId: string
  approvedLicenses: License[]
}) {
  const qc = useQueryClient()
  const [requested, setRequested] = useState(false)
  const media = item.media_items
  const rights = media.rights_profiles?.[0]

  // Check if this item has an approved license
  const license = approvedLicenses.find(l => l.media_item_id === media.id)

  const requestMutation = useMutation({
    mutationFn: async () => {
      await supabase.from('push_queue').update({ status: 'actioned', actioned_at: new Date().toISOString() }).eq('id', item.id)
      const { error } = await supabase.from('license_requests').insert({
        media_item_id: media.id,
        requester_id: participantId,
        status: 'pending',
        message: 'Requesting license via The Room inbox',
      })
      if (error) throw error
    },
    onSuccess: () => { setRequested(true); qc.invalidateQueries({ queryKey: ['inbox'] }) },
  })

  const dismissMutation = useMutation({
    mutationFn: async ({ pushId, reason }: { pushId: string; reason: string }) => {
      await supabase.from('push_queue').update({ status: 'dismissed' }).eq('id', pushId)
      // Log feedback for AI training
      await supabase.from('buyer_feedback').insert({
        push_queue_id:  pushId,
        participant_id: participantId,
        decision:       'reject',
        reason_code:    reason,
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox'] }),
  })

  const markSeenMutation = useMutation({
    mutationFn: async () => {
      if (item.status === 'queued') {
        await supabase.from('push_queue').update({ status: 'delivered', delivered_at: new Date().toISOString() }).eq('id', item.id)
      }
    },
  })

  // Mark as delivered when card renders
  if (item.status === 'queued' && !markSeenMutation.isPending && !markSeenMutation.isSuccess) {
    markSeenMutation.mutate()
  }

  const scoreColor = item.match_score >= 0.7 ? 'text-green-400' : item.match_score >= 0.5 ? 'text-yellow-400' : 'text-neutral-500'

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
      {/* If licensed, show full image */}
      {license ? (
        <div className="relative aspect-video bg-neutral-900 overflow-hidden">
          <img src={media.cloudinary_url} alt="" className="w-full h-full object-cover" />
          <div className="absolute top-2 right-2">
            <span className="text-xs bg-green-900/80 text-green-300 px-2 py-0.5 rounded-full border border-green-700">
              ✓ Licensed
            </span>
          </div>
        </div>
      ) : (
        <WatermarkedPreview url={media.cloudinary_url} mediaType={media.media_type} />
      )}

      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-white font-medium text-sm line-clamp-2">{media.ai_description || media.file_name}</p>
            {media.participants && (
              <p className="text-neutral-500 text-xs mt-0.5">by {media.participants.name}</p>
            )}
          </div>
          <span className={cn('text-sm font-bold shrink-0', scoreColor)}>
            {Math.round(item.match_score * 100)}%
          </span>
        </div>

        {media.ai_tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {media.ai_tags.slice(0, 4).map(tag => (
              <span key={tag} className="px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400 text-xs">{tag}</span>
            ))}
          </div>
        )}

        {rights && (
          <div className="flex items-center gap-3 text-xs text-neutral-600">
            <span className="capitalize">{rights.usage_type}</span>
            {rights.credit_required && <span>· Credit required</span>}
          </div>
        )}

        {/* Action area */}
        {license ? (
          <a
            href={license.download_url ?? media.cloudinary_url}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-green-600 hover:bg-green-500 text-white text-sm font-semibold transition-colors"
          >
            <Download size={15} />
            Download
          </a>
        ) : item.status === 'actioned' || requested ? (
          <div className="text-center py-2 text-sm text-amber-400">⏳ License request pending</div>
        ) : (
          <div className="flex gap-2">
            <Button
              onClick={() => requestMutation.mutate()}
              disabled={requestMutation.isPending}
              className="flex-1 bg-pink-600 hover:bg-pink-500 text-white text-sm"
            >
              {requestMutation.isPending ? <Spinner size={14} /> : 'Request license'}
            </Button>
            <div className="flex flex-col gap-1">
              {[
                { code: 'not_our_style', label: 'Not our style' },
                { code: 'ran_similar',   label: 'Ran similar' },
                { code: 'quality_issue', label: 'Quality' },
              ].map(r => (
                <button
                  key={r.code}
                  onClick={() => dismissMutation.mutate({ pushId: item.id, reason: r.code })}
                  disabled={dismissMutation.isPending}
                  className="text-[10px] text-neutral-600 hover:text-neutral-400 transition-colors text-left"
                >
                  ✕ {r.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function InboxPage() {
  const { participant } = useAuthStore()
  const [filter, setFilter] = useState<'active' | 'all'>('active')

  const { data: recipientData } = useQuery({
    queryKey: ['recipient', participant?.id],
    queryFn: async () => {
      if (!participant?.id) return null
      const { data } = await supabase
        .from('recipients')
        .select('*')
        .eq('participant_id', participant.id)
        .single()
      return data
    },
    enabled: !!participant?.id,
  })

  const { data: inbox = [], isLoading } = useQuery({
    queryKey: ['inbox', recipientData?.id, filter],
    queryFn: async () => {
      if (!recipientData?.id) return []
      let q = supabase
        .from('push_queue')
        .select(`
          id, match_score, match_reason, status, queued_at,
          media_items(
            id, cloudinary_url, media_type, file_name,
            ai_tags, ai_description, ai_mood, ai_route_score,
            participants(name, handle),
            rights_profiles(usage_type, credit_required, embargo_until)
          )
        `)
        .eq('recipient_id', recipientData.id)
        .order('match_score', { ascending: false })

      if (filter === 'active') q = q.in('status', ['queued', 'delivered', 'actioned'])

      const { data, error } = await q
      if (error) throw error
      return (data ?? []) as unknown as PushItem[]
    },
    enabled: !!recipientData?.id,
    refetchInterval: 20_000,
  })

  // Fetch approved licenses for this recipient
  const { data: approvedLicenses = [] } = useQuery<License[]>({
    queryKey: ['licenses', participant?.id],
    queryFn: async () => {
      if (!participant?.id) return []
      const { data, error } = await supabase
        .from('licenses')
        .select('id, media_item_id, download_url')
        .eq('licensee_id', participant.id)
      if (error) throw error
      return data ?? []
    },
    enabled: !!participant?.id,
    refetchInterval: 20_000,
  })

  if (!participant) {
    return <div className="flex items-center justify-center h-64 text-neutral-500">Sign in to see your inbox</div>
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Inbox</h1>
          <p className="text-neutral-400 text-sm mt-0.5">Content matched and pushed to you</p>
        </div>
        {approvedLicenses.length > 0 && (
          <span className="text-xs text-green-400">{approvedLicenses.length} licensed</span>
        )}
      </div>

      <div className="flex gap-2">
        {(['active', 'all'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'px-4 py-1.5 rounded-full text-sm capitalize transition-all',
              filter === f ? 'bg-pink-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:text-white'
            )}
          >
            {f === 'active' ? 'Active' : 'All'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : inbox.length === 0 ? (
        <div className="text-center py-16 text-neutral-500">
          <p className="text-4xl mb-4">📭</p>
          <p className="font-medium">Nothing here yet</p>
          <p className="text-sm mt-1">Content matched to your interests will appear here</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {inbox.map(item => (
            <PushCard
              key={item.id}
              item={item}
              participantId={participant.id}
              approvedLicenses={approvedLicenses}
            />
          ))}
        </div>
      )}
    </div>
  )
}
